import {
  Box,
  Button,
  Container,
  Paper,
  TextField,
  Stack,
  InputAdornment,
  IconButton,
  Typography,
  Link,
} from "@mui/material";
import { useState } from "react";
import { useLogin } from "@refinedev/core";
import { Link as RouterLink } from "react-router-dom";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
// Import the logo image
import logo from "../../assets/logo_transparent_black.png";

export const Login = () => {
  const { mutate: login, isLoading } = useLogin({
    mutationOptions: {
      onSuccess: (data) => {
        const success = data.success;

        if (!success && data.error?.message) {
          setLoginError(data.error?.message);

          return;
        }
      },
    },
  });

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);

  // Function to handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoginError(null);

    login({ email, password });
  };

  const handleTogglePassword = () => {
    setShowPassword(!showPassword);
  };

  return (
    <Container
      sx={{
        width: "100%",
        maxWidth: "450px !important",
        height: "95vh",
        display: "flex",
        alignItems: "center",
      }}
    >
      <Box sx={{ width: "100%", display: "flex", flexDirection: "column" }}>
        <Box sx={{ mb: 3, display: "flex", justifyContent: "center" }}>
          <img
            src={logo}
            alt="Fashion Words Logo"
            style={{
              maxWidth: "150px",
              height: "auto",
              objectFit: "contain",
            }}
          />
        </Box>
        <Paper
          elevation={3}
          sx={{
            p: 4,
            width: "100%",
            backgroundColor: "primary.dark",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              pt: 2,
              pb: 4,
            }}
          >
            <Typography
              variant="h1"
              sx={{
                textAlign: "center",
                color: "primary.light",
                fontSize: "1.75rem",
                fontWeight: "bold",
                mb: 2,
              }}
            >
              Welcome to MyContent
            </Typography>

            <Typography
              variant="h2"
              sx={{
                textAlign: "center",
                color: "primary.light",
                fontSize: "1rem",
              }}
            >
              Create. Manage. Approve.
            </Typography>
          </Box>
          <Box component="form" onSubmit={handleSubmit}>
            <Stack spacing={3}>
              <TextField
                fullWidth
                id="email"
                label="Indirizzo Email"
                variant="filled"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setLoginError(null);
                }}
                sx={{ mb: 1 }}
              />

              <TextField
                fullWidth
                id="password"
                label="Password"
                type={showPassword ? "text" : "password"}
                variant="filled"
                autoComplete="password"
                required
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setLoginError(null);
                }}
                slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={handleTogglePassword}
                          edge="end"
                        >
                          {showPassword ? (
                            <VisibilityOffIcon />
                          ) : (
                            <VisibilityIcon />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  },
                }}
              />

              {loginError && (
                <Typography
                  sx={{
                    color: "primary.light",
                    fontSize: "0.875rem",
                    textAlign: "center",
                    pb: 1,
                    borderBottom: "4px solid #D0021B",
                  }}
                >
                  {loginError}
                </Typography>
              )}

              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={isLoading}
                sx={{
                  mt: 2,
                  py: 1.5,
                }}
              >
                {isLoading ? "Logging in..." : "Login"}
              </Button>

              <Box sx={{ textAlign: "center", mt: 2 }}>
                <Link
                  component={RouterLink}
                  to="/forgot-password"
                  sx={{
                    color: "primary.light",
                    textDecoration: "none",
                    fontSize: "0.9rem",
                    "&:hover": {
                      textDecoration: "underline",
                    },
                  }}
                >
                  Forgot Password?
                </Link>
              </Box>
            </Stack>
          </Box>
        </Paper>

        <Box sx={{ textAlign: "center", mt: 1 }}>
          <Link
            href={`${import.meta.env.VITE_API_URL}/terms/document`}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              color: "primary.dark",
              textDecoration: "none",
              fontSize: "0.8rem",
              "&:hover": {
                textDecoration: "underline",
              },
            }}
          >
            Terms and Conditions
          </Link>
        </Box>
      </Box>
    </Container>
  );
};
